import { Box, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Link } from 'react-router-dom';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { usePennames } from '../../hooks/queries/usePenname';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import useTablePagination from '../../hooks/useTablePagination';
import { IPenname } from '../../services/types';
import { RouteNames } from '../config';

interface IProps {
  onRowClick: (penname: IPenname) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  search: {
    width: 360,
    marginBottom: 20,
  },
  idTableCell: {
    width: 80,
  },
  createdAtTableCell: {
    width: 180,
  },
  tableRow: {
    height: 52,
  },
}));

const PennameList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { onRowClick } = props;

  const [searchKey, setSearchKey] = useQueryStringState<string>('searchKey');
  const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination();

  const { data, isLoading } = usePennames(page, rowsPerPage, searchKey);

  const pennames = data?.items;
  const totalCount = data?.totalCount;

  const handleRowClick = (penname: IPenname) => () => onRowClick(penname);

  const handleSearchSubmit = (query: string) => {
    setSearchKey(query);
    setPage(1);
  };

  const handleSearchValueChange = (query: string) => {
    if (!query) {
      handleSearchCancel();
    }
  };

  const handleSearchCancel = () => {
    setPage(1);
    setSearchKey(undefined);
  };

  return (
    <Box>
      <SearchTextField
        defaultValue={searchKey}
        onValueChange={handleSearchValueChange}
        placeholder="Search a Pen Name"
        className={classes.search}
        onValueSubmit={handleSearchSubmit}
        onCancel={handleSearchCancel}
      />
      <Paper>
        <Table>
          <TableHead>
            <TableCell align="center" padding="none" className={classes.idTableCell}>
              ID
            </TableCell>
            <TableCell>Pen Name</TableCell>
            <TableCell>Assigned Accounts</TableCell>
            <TableCell>Pen Name Edit Account</TableCell>
            <TableCell className={classes.createdAtTableCell}>Created At</TableCell>
          </TableHead>
          <TableBody>
            {isLoading && <TableSkeleton rows={rowsPerPage} columns={5} />}
            {!isLoading &&
              pennames &&
              pennames.map(penname => {
                const { id, name, licensees, editor, createdAt } = penname;
                return (
                  <TableRow
                    key={id}
                    component={Link}
                    to={`${RouteNames.Penname}/v/${id}`}
                    hover
                    onClick={handleRowClick(penname)}
                    className={classes.tableRow}
                  >
                    <TableCell align="center" padding="none">
                      {id}
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{licensees.map(({ username }) => username).join(', ')}</TableCell>
                    <TableCell>{editor.username}</TableCell>
                    <TableCell>{moment(createdAt).format('LLL')}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {!isLoading && isEmpty(pennames) && (
          <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1">This Pen Name does not exist. Please try again.</Typography>
          </Box>
        )}
        {totalCount !== undefined && (
          <TablePagination
            count={totalCount || -1} // an unknown number of items, provide -1. (https://material-ui.com/api/table-pagination/)
            rowsPerPage={rowsPerPage}
            page={page - 1} // TablePagination has the zero-based index of the current page.
            onChangePage={(_: unknown, p: number) => setPage(p + 1)}
            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
              const rpp = parseInt(e.target.value, 10);
              setRowsPerPage(rpp);
              setPage(1);
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default PennameList;
